import type {ReactElement} from 'react';
import {IconSize} from '@Components/icon-v2/icon.types';
import {PASSWORD_POLICY_ITEM_STATUS} from '@Components/signup-form/signup-form.types';
import {Text, TextSize} from '../../../text';
import {Icon} from '../../../icon-v2';
import styles from './password-policy-item.module.scss';

interface PasswordPolicyItemProps {
  text: string;
  status: PASSWORD_POLICY_ITEM_STATUS;
  'data-testid'?: string;
}

export function PasswordPolicyItem({text, status, 'data-testid': dataTestId}: PasswordPolicyItemProps): ReactElement {
  const getIconName = (): string => {
    switch (status) {
      case PASSWORD_POLICY_ITEM_STATUS.COMPLETE:
      case PASSWORD_POLICY_ITEM_STATUS.MATCH:
        return 'icon-check-circle';
      case PASSWORD_POLICY_ITEM_STATUS.INCOMPLETE:
        return 'icon-x-circle';
      case PASSWORD_POLICY_ITEM_STATUS.MISMATCH:
        return 'icon-exclamation-triangle';
      case PASSWORD_POLICY_ITEM_STATUS.IN_PROGRESS:
      default:
        return 'icon-circle';
    }
  };

  const getClassNameForStatus = (): string => {
    switch (status) {
      case PASSWORD_POLICY_ITEM_STATUS.COMPLETE:
      case PASSWORD_POLICY_ITEM_STATUS.MATCH:
        return 'ct-success-primary';
      case PASSWORD_POLICY_ITEM_STATUS.INCOMPLETE:
      case PASSWORD_POLICY_ITEM_STATUS.MISMATCH:
        return 'ct-error-primary';
      case PASSWORD_POLICY_ITEM_STATUS.IN_PROGRESS:
      default:
        return 'ct-neutral-primary';
    }
  };

  /**
   * The criteria list and the single match/mismatch verdict are spaced differently:
   * 8px between icon and label in the list, 4px on the verdict.
   */
  const getRowSpacingClassName = (): string => {
    switch (status) {
      case PASSWORD_POLICY_ITEM_STATUS.MATCH:
      case PASSWORD_POLICY_ITEM_STATUS.MISMATCH:
        return styles.verdictRow;
      default:
        return styles.criterionRow;
    }
  };

  return (
    <div className={`${styles.policyItem} ${getRowSpacingClassName()} ${getClassNameForStatus()}`} data-testid={dataTestId}>
      <Icon icon={getIconName()} size={IconSize.SIZE_ICON_16} />
      <Text val={text} size={TextSize.SM} />
    </div>
  );
}
